/**
 * @fileoverview Input/output schemas for weave-snapshotter-short-v1.
 *
 * Short-form variant of the weave snapshotter. No storyline concepts, no
 * beat-level arc tracking. Focuses on simplified character arc progress
 * observations. Uses writtenPlot as the structural reference framework
 * instead of storylines.
 *
 * Plot thread lifecycle management has been moved to the dedicated
 * plot-thread-snapshotter-v1 agent. Threads are passed as read-only context.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Simplified thread representation for the weave agent input.
 * Read-only context — thread lifecycle is managed by plot-thread-snapshotter-v1.
 */
const WeaveExistingThreadSchema = z.object({
  id: z.string(),
  title: z.string(),
  threadQuestion: z.string(),
  summary: z.string(),
  status: z.enum(['active', 'resolved', 'abandoned']),
});

/**
 * Simplified character arc data for short-form weave agent input.
 *
 * Short-form projects don't need beat-level tracking (the character equivalent
 * of storyline beats). Only arc name and summary are needed — the agent
 * produces free-text progress observations rather than beat-mapped touches.
 */
const WeaveCharacterArcShortSchema = z.object({
  characterId: z.string(),
  characterName: z.string(),
  arcName: z.string(),
  arcSummary: z.string(),
});

/**
 * Stage 1 event data piped from the parallel snapshotters.
 */
const MajorPlotEventSchema = z.object({
  id: z.string(),
  description: z.string(),
});

const MajorCharacterEventsSchema = z.object({
  characterName: z.string(),
  events: z.array(z.object({
    id: z.string(),
    description: z.string(),
  })),
});

/**
 * Full input for the short-form weave snapshotter flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,

  // Structural context (no storylines)
  existingThreads: z.array(WeaveExistingThreadSchema).optional().describe(
    'All plot threads (read-only context for cross-referencing with character arcs).'
  ),
  characterArcs: z.array(WeaveCharacterArcShortSchema).optional().describe(
    'Character arcs (name + summary only) for lightweight arc progress observation.'
  ),
  writtenPlot: z.string().optional().describe(
    'The writtenPlot Markdown, structured as 7 phase headings. ' +
    'Used as a reference framework for understanding narrative structure.'
  ),

  // Stage 1 piped outputs
  majorPlotEvents: z.array(MajorPlotEventSchema).optional().describe(
    'Major plot events extracted by the plot snapshotter (Stage 1).'
  ),
  majorCharacterEvents: z.array(MajorCharacterEventsSchema).optional().describe(
    'Per-character major events extracted by the character snapshotter (Stage 1).'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Short-form weave agent output.
 *
 * Produces characterArcProgress (simplified).
 * No storyline sections, no beat-level arc tracking.
 *
 * characterArcProgress replaces the long-form characterArcTouches:
 * instead of mapping evidence to specific beats (foreshadowed/advanced/fulfilled),
 * it produces per-character free-text observations about arc progression.
 */
export const OutputSchema = z.object({
  characterArcProgress: z.array(z.object({
    characterId: z.string().describe('ID of the character.'),
    arcName: z.string().describe('Name of the character arc.'),
    arcSummary: z.string().describe('Summary of the planned arc transformation.'),
    progressNote: z.string().describe(
      'Brief observation about where the character stands in their arc ' +
      'based on the chapter content. Focus on internal shifts and transformation.'
    ),
    evidence: z.string().describe('Brief quote or paraphrase from the chapter text.'),
  })).describe('Simplified character arc progress observations for short-form stories.'),
});

export type Output = z.infer<typeof OutputSchema>;
